import React, { useState, useRef, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import userQueries from '../../queries/userQueries';
import Loader from '../Loader/Loader';
import RichTextEditor from "../Rte/Rteeditor";
import Highlight from "../ToggleSwitch/HighlightBt";
import { BASE_URL } from "../../apis/api";


const EditNews = () => {
    const [susmsg, setSusmsg] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageUploadSuccess, setImageUploadSuccess] = useState(false);
    const [imageError, setImageError] = useState('');
    const [loading, setLoading] = useState(false);
    const imageRef = useRef(null);
    const [content, setContent] = useState('');
    const [contentError,setContentError] = useState('');
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);


    const { id } = useParams();
    const navigate = useNavigate();

    const { register, reset, handleSubmit, control, getValues,formState: { errors } } = useForm();

    const MAX_FILE_SIZE = 350 * 1024; 

    const newsdata = userQueries.useUpdatenewsMutation(
        (response) => {
           
            if (response?.data) {
                let datas = ({
                    id: id,
                    shortdescription: response?.data.shortdescription,
                    maindescription: response?.data.maindescription,
                    publishstatus: response?.data.publishstatus,
                    displayorder: response?.data.displayorder,
                    highlight: response?.data.highlight || 0
                });
                setContent(response?.data.maindescription);
                reset({ ...datas });
                setCurrentImage(response?.data.image || null);
                setCurrentImageUrl(response?.data.image ? `${BASE_URL}/${response.data.image}` : null);
            }
        }
    );

    useEffect(() => {
        newsdata.mutateAsync({ id });
    }, [id]);

    const editSpeaker = userQueries.useUpdatenewsMutation(
        (response) => {
            if (response?.message === "Success") {
                setSusmsg(true);
                alert(response.data.publishstatus === '0' ? "Saved as draft" : "Published successfully");
                navigate('/newslist');
            }
            
        }
    );

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedImage(file);
            
            // Create a preview URL for the newly selected image
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
            const allowedTypes = ['image/jpeg', 'image/jpg'];
                if (!allowedTypes.includes(file.type)) {
                    setImageError("Only JPEG, JPG files are allowed.");
                    setLoading(false);  // Re-enable buttons if validation fails
                    return false;
                }if (file.size > MAX_FILE_SIZE) {
                    setImageError("Image size should not exceed 350KB.");
                    setLoading(false);
                    return false;
                }
            
        }
    };

    const handleFormSubmit = async (data, publishStatus) => {

        if(selectedImage){
            if (selectedImage.size > MAX_FILE_SIZE) {
                setImageError("Image size should not exceed 350KB."); 
                console.log(imageError);
                return;
            }
        }
        setLoading(true);  // Disable buttons immediately after submitting

        if(publishStatus === 1)
        {
            
            if (!content) {
                setImageError("");  // Clear image error
                setContentError("Content is required.");  // Set content error
                setLoading(false);
                return false;
            }
            
            else if(!selectedImage && !currentImage)
            {
                setImageError("Image Required");
                    setLoading(false);  // Re-enable buttons if validation fails
                    return false;
            }
        }

        const datavalues = {
            id: id,
            shortdescription: data.shortdescription,
            maindescription: content,
            news: selectedImage,
            publishstatus: publishStatus,
            displayorder: data.displayorder !== null ? data.displayorder : null,
            highlight: data.highlight
        };
    
        try {
            await editSpeaker.mutateAsync(datavalues);
            
        } catch (error) {
            console.error("Error updating news:", error);
        } finally {
            setLoading(false);  // Re-enable buttons after completion
        }
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);
    
    return (
        <>
            <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">
                Update News
            </h4>
            <form className="max-w-lg mx-auto grid grid-cols-2 gap-4">
                <div className="mb-5 col-span-2">
                    <label htmlFor="shortdescription" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Heading <span className="text-sm text-gray-500"></span>
                    </label>
                    <textarea 
                        id="shortdescription" 
                        {...register("shortdescription", { 
                            required: "Heading is required",
                            maxLength: {
                                value: 256,
                                message: "Heading cannot exceed 256 characters"
                            }
                        })} 
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" 
                        rows="4"
                    />
                    {errors.shortdescription && <div className="text-red-600 text-s">{errors.shortdescription.message}</div>}
                </div>

                <div className="mb-5 col-span-2">
                    <label htmlFor="maindescription" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Content <span className="text-sm text-gray-500"></span>
                    </label>
                    <RichTextEditor
                        onChange={(value) => setContent(value)}
                        name="content"
                        value={content}
                    />
                    {errors.content && <div className="text-red-600 text-s">{errors.maindescription.message}</div>}
                </div>
                {contentError && <div className="text-red-600 text-s">{contentError}</div>}

                <div className="mb-5 col-span-2">
                    <label htmlFor="image" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Upload Image
                    </label>
                    {/* Image Preview Section */}
                    <div className="mb-4">
                        {(previewUrl || currentImageUrl) && (
                            <div className="relative w-48 h-48 mb-4">
                                <img
                                    src={previewUrl || currentImageUrl}
                                    alt="Speaker preview"
                                    className="w-full h-full object-cover rounded-lg shadow-md"
                                />
                            </div>
                        )}
                    </div>
                    <input
                        type="file"
                        id="image"
                        ref={imageRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                    />
                    
                    {imageError && <div className="text-red-600 text-s">{imageError}</div>}
                    {imageUploadSuccess && (
                        <div className="mt-2 text-sm text-green-600">
                            Image uploaded successfully!
                        </div>
                    )}
                    <span className="form-text text-warning small">
                    Recommended: Use landscape images under 350 KB.
                  </span>
                </div>
                <div className="mb-5">
                    <label htmlFor="displayorder" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Display Order</label>
                    <input 
                        type="number" 
                        id="displayorder" 
                        {...register("displayorder", { required: false })} 
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5" 
                    />
                   
                </div>
                <div className="flex items-center space-x-2">
                    <Controller
                        name="highlight"
                        control={control}
                        render={({ field }) => (
                            <Highlight
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                            />
                        )}
                        />
                        <label htmlFor="highlight" className="text-sm font-medium text-gray-700">
                        Check this to highlight the news.
                        </label>
                </div>

                <button 
                    type="button"
                    onClick={() => handleFormSubmit(getValues(), 0)}
                    className="col-span-2 w-full text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-3"
                >
                    Save as draft
                </button>

                <button 
                    type="button"
                    onClick={handleSubmit((data) => handleFormSubmit(data, 1))}
                    className="col-span-2 w-full text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                    Save and publish
                </button>

                {susmsg && (
                    <div className="text-green-600 text-lg ml-6 text-center inline-block whitespace-nowrap col-span-2">
                        News updated successfully
                    </div>
                )}
            </form>
            {loading && (
                <Loader />  
            )}
        </>
    );
};

export default EditNews;
